import type { ComponentProps, ReactElement, ReactNode } from 'react'
import clsx from 'clsx'
import styled, { css } from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

import _BaseButton from './BaseButton'

const fitStyles = css`
  &.fit-flex {
    display: flex;
    width: 100%;
  }

  &.fit-full-width {
    width: 100%;
  }

  &.fit-mobile-full-width {
    width: 100%;

    ${mediaQueryUp.tablet} {
      width: auto;
    }
  }

  &.fit-desktop-full-width {
    ${mediaQueryUp.tablet} {
      width: 100%;
    }
  }
`

const StyledBaseButton = styled(_BaseButton)`
  &:where(.kind-primary, .kind-secondary) {
    min-width: ${rem(80)};
  }

  &:where(.size-xsmall, .size-small) {
    --text-button-icon-size: ${rem(16)};
  }

  &:where(.size-medium) {
    --text-button-icon-size: ${rem(20)};
  }

  &:where(.size-large) {
    --text-button-icon-size: ${rem(24)};
  }

  > svg:first-of-type,
  > svg:last-of-type {
    flex-shrink: 0;
    height: var(--text-button-icon-size, auto);
    width: var(--text-button-icon-size, auto);
  }

  &.has-icon-start > :first-child {
    margin-inline-end: ${rem(8)};
  }

  &.has-icon-end > :last-child {
    margin-inline-start: ${rem(8)};
  }

  @media print {
    display: none;
  }

  ${fitStyles}
`

const UnderlineText = styled.span`
  text-decoration: underline;
  text-underline-offset: ${rem(6)};
`

interface Props extends Omit<ComponentProps<typeof _BaseButton>, 'children'>, Utils.PropsWithDataAttributes {
  children: ReactNode
  startIcon?: ReactElement | false
  endIcon?: ReactElement | false
  fit?: 'auto' | 'full-width' | 'flex' | 'mobile-full-width' | 'desktop-full-width'
}

function TextButton({
  children,
  className,
  endIcon,
  fit = 'auto',
  kind,
  startIcon,
  ...props
}: Props) {
  const hasStartIcon = !!startIcon
  const hasEndIcon = !!endIcon
  const hasAnyIcon = hasStartIcon || hasEndIcon
  const content = !hasAnyIcon && kind === 'tertiary' ? <UnderlineText>{children}</UnderlineText> : children

  return <StyledBaseButton
    {...props}
    className={clsx(
      className,
      `fit-${fit}`,
      {
        'has-icon-start': hasStartIcon,
        'has-icon-end': hasEndIcon,
      },
    )}
    kind={kind}
  >
    {startIcon}
    {content}
    {endIcon}
  </StyledBaseButton>
}

export default TextButton
